// Typed HTTP transport for the GHL-Lite operator console.
//
// Rule: this file is the ONLY module in the SPA that calls fetch().
// Components and pages must not import fetch directly. Reads/writes
// flow through hooks in `src/client/data/*` which call into here.
//
// Auth: every request to `/api/...` carries the Clerk session JWT in
// the `Authorization` header. The backend's `clerkMiddleware` +
// `findConsoleAdmin` revalidate it on each call.

import {
  ApiRequestError,
  type AddCustomerRequest,
  type AddCustomerResult,
  type ApiAuthConfig,
  type ConnectionCheck,
  type GhlStatus,
  type IngestStatus,
  type RuntimeStatus,
  type SiteInventory,
} from "./types";

export type GetClerkToken = () => Promise<string | null>;

interface ApiOptions {
  /** Function that returns a Clerk session JWT, or null if signed out. */
  getToken: GetClerkToken;
}

async function requestJson<T>(
  path: string,
  init: RequestInit,
  options: ApiOptions,
): Promise<T> {
  const token = await options.getToken();
  const headers: Record<string, string> = {
    accept: "application/json",
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.authorization = `Bearer ${token}`;
  const response = await fetch(path, { ...init, headers });
  if (!response.ok) {
    let code: string | undefined;
    let message = `${path} returned HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body && typeof body === "object" && "error" in body) {
        code = String((body as { error: unknown }).error ?? "");
        if (code) message = `${path} ${code}`;
      }
    } catch {
      // ignore — keep generic message
    }
    throw new ApiRequestError(path, response.status, message, code);
  }
  return (await response.json()) as T;
}

export function createApiClient(options: ApiOptions) {
  return {
    async getAuthConfig(): Promise<ApiAuthConfig> {
      // Public endpoint — no token required, but sending one is harmless.
      return requestJson<ApiAuthConfig>("/api/auth/config", { method: "GET" }, options);
    },
    async getStatus(): Promise<RuntimeStatus> {
      return requestJson<RuntimeStatus>("/api/status", { method: "GET" }, options);
    },
    async getGhlStatus(): Promise<GhlStatus> {
      return requestJson<GhlStatus>("/api/ghl/status", { method: "GET" }, options);
    },
    async getSites(): Promise<SiteInventory> {
      return requestJson<SiteInventory>("/api/sites", { method: "GET" }, options);
    },
    async getIngestStatus(): Promise<IngestStatus> {
      return requestJson<IngestStatus>("/api/ingest/status", { method: "GET" }, options);
    },
    async runCheckConnection(): Promise<ConnectionCheck> {
      return requestJson<ConnectionCheck>(
        "/api/ghl/check-connection",
        { method: "POST" },
        options,
      );
    },
    async addCustomer(input: AddCustomerRequest): Promise<AddCustomerResult> {
      return requestJson<AddCustomerResult>(
        "/api/customers",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(input),
        },
        options,
      );
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

export const publicApiClient = createApiClient({
  getToken: async () => null,
});

export { ApiRequestError };
